// src/pages/Users/Techniques/utils/techniquesUtils.js

export const DEFAULT_TECHNIQUES_STATE = {
  search: "",
  grade: "Toutes les ceintures",
  family: "Toutes les familles",
  sort: "beltFamily",
  perPage: 24,
};

export const FALLBACK_TECHNIQUE_IMG = "/img/no-image.jpg";

export const BELT_BASE = [
  "blanche",
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

export const FAMILY_ORDER = [
  "Jambe",
  "Hanche",
  "Epaule",
  "Bras",
  "Sutemi Waza",
  "Immobilisation",
  "Shime Waza",
  "Kansetsu-Waza",
  "Self Défense",
  "Kata",
  "Vocabulaire",
  "Les Bases",
  "Ukemi",
];

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function strip(value = "") {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function cleanTechniqueText(value = "") {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/^"+|"+$/g, "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function truncateText(value = "", maxLength = 120) {
  const cleanValue = cleanTechniqueText(value);

  if (!cleanValue) {
    return "";
  }

  if (cleanValue.length <= maxLength) {
    return cleanValue;
  }

  return `${cleanValue.slice(0, maxLength).trim()}…`;
}

export function beltRank(ceinture = "") {
  const normalized = strip(ceinture);

  if (!normalized) {
    return Number.POSITIVE_INFINITY;
  }

  const danMatch = normalized.match(/(\d+)\s*(?:er|e|eme|ème)?\s*dan/);

  if (danMatch) {
    const danNumber = Number.parseInt(danMatch[1], 10) || 1;

    return 6 + danNumber;
  }

  const colorsFound = BELT_BASE.map((color, index) =>
    normalized.includes(color) ? index : -1,
  ).filter((index) => index >= 0);

  if (colorsFound.length === 1) {
    return colorsFound[0];
  }

  if (colorsFound.length >= 2) {
    return (
      colorsFound.reduce((total, value) => total + value, 0) /
      colorsFound.length
    );
  }

  return Number.POSITIVE_INFINITY;
}

export function familyRank(family = "") {
  const normalized = strip(family);

  const index = FAMILY_ORDER.findIndex((item) => strip(item) === normalized);

  return index >= 0 ? index : 999;
}

export function buildTechniqueOptions(techniques = []) {
  const safeTechniques = Array.isArray(techniques)
    ? techniques.filter(Boolean)
    : [];

  const grades = Array.from(
    new Set(
      safeTechniques.map((technique) => technique?.ceinture).filter(Boolean),
    ),
  ).sort(
    (firstValue, secondValue) =>
      beltRank(firstValue) - beltRank(secondValue) ||
      collatorFR.compare(firstValue, secondValue),
  );

  const families = Array.from(
    new Set(
      safeTechniques.map((technique) => technique?.famille).filter(Boolean),
    ),
  ).sort(
    (firstValue, secondValue) =>
      familyRank(firstValue) - familyRank(secondValue) ||
      collatorFR.compare(firstValue, secondValue),
  );

  return {
    grades: [DEFAULT_TECHNIQUES_STATE.grade, ...grades],
    families: [DEFAULT_TECHNIQUES_STATE.family, ...families],
  };
}

export function filterAndSortTechniques(
  techniques = [],
  ui = DEFAULT_TECHNIQUES_STATE,
) {
  const safeUi = {
    ...DEFAULT_TECHNIQUES_STATE,
    ...(ui || {}),
  };

  const searchTerm = strip(safeUi.search);

  const filtered = (Array.isArray(techniques) ? techniques : []).filter(
    (technique) => {
      if (!technique) {
        return false;
      }

      const searchableText = [
        technique.nom,
        technique.nom_japonais,
        technique.name,
        technique.traduction,
        technique.famille,
        technique.ceinture,
        technique.information,
      ]
        .map(strip)
        .join(" ");

      const matchesSearch = !searchTerm || searchableText.includes(searchTerm);

      const matchesGrade =
        safeUi.grade === DEFAULT_TECHNIQUES_STATE.grade ||
        technique.ceinture === safeUi.grade;

      const matchesFamily =
        safeUi.family === DEFAULT_TECHNIQUES_STATE.family ||
        technique.famille === safeUi.family;

      return matchesSearch && matchesGrade && matchesFamily;
    },
  );

  filtered.sort((firstTechnique, secondTechnique) => {
    const firstName = String(firstTechnique?.nom || "");

    const secondName = String(secondTechnique?.nom || "");

    if (safeUi.sort === "alpha") {
      return collatorFR.compare(firstName, secondName);
    }

    if (safeUi.sort === "grade") {
      return (
        beltRank(firstTechnique?.ceinture) -
          beltRank(secondTechnique?.ceinture) ||
        collatorFR.compare(firstName, secondName)
      );
    }

    if (safeUi.sort === "family") {
      return (
        familyRank(firstTechnique?.famille) -
          familyRank(secondTechnique?.famille) ||
        collatorFR.compare(firstName, secondName)
      );
    }

    const beltDifference =
      beltRank(firstTechnique?.ceinture) - beltRank(secondTechnique?.ceinture);

    if (beltDifference !== 0) {
      return beltDifference;
    }

    const familyDifference =
      familyRank(firstTechnique?.famille) -
      familyRank(secondTechnique?.famille);

    if (familyDifference !== 0) {
      return familyDifference;
    }

    return collatorFR.compare(firstName, secondName);
  });

  return filtered;
}

export function hasActiveFilters(ui = DEFAULT_TECHNIQUES_STATE) {
  const safeUi = {
    ...DEFAULT_TECHNIQUES_STATE,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.search) ||
    safeUi.grade !== DEFAULT_TECHNIQUES_STATE.grade ||
    safeUi.family !== DEFAULT_TECHNIQUES_STATE.family ||
    safeUi.sort !== DEFAULT_TECHNIQUES_STATE.sort
  );
}

export function countActiveFilters(ui = DEFAULT_TECHNIQUES_STATE) {
  const safeUi = {
    ...DEFAULT_TECHNIQUES_STATE,
    ...(ui || {}),
  };

  let count = 0;

  if (safeUi.search) count += 1;

  if (safeUi.grade !== DEFAULT_TECHNIQUES_STATE.grade) {
    count += 1;
  }

  if (safeUi.family !== DEFAULT_TECHNIQUES_STATE.family) {
    count += 1;
  }

  if (safeUi.sort !== DEFAULT_TECHNIQUES_STATE.sort) {
    count += 1;
  }

  return count;
}

export function buildShareUrl(ui = DEFAULT_TECHNIQUES_STATE) {
  const params = new URLSearchParams();

  if (ui.search) {
    params.set("q", ui.search);
  }

  if (ui.grade !== DEFAULT_TECHNIQUES_STATE.grade) {
    params.set("grade", ui.grade);
  }

  if (ui.family !== DEFAULT_TECHNIQUES_STATE.family) {
    params.set("famille", ui.family);
  }

  if (ui.sort !== DEFAULT_TECHNIQUES_STATE.sort) {
    params.set("sort", ui.sort);
  }

  const query = params.toString();

  return `${window.location.origin}${window.location.pathname}${
    query ? `?${query}` : ""
  }`;
}

export function resolveTechniqueAssetUrl(source) {
  if (!source) return "";

  const value = String(source).trim().replace(/\\/g, "/");

  /*
   * URL déjà complète :
   * Cloudinary, serveur distant, YouTube, etc.
   */
  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  /*
   * Images contenues dans le dossier public du front :
   *
   * public/img/techniques/image.jpg
   * devient accessible avec :
   * /img/techniques/image.jpg
   */
  if (value.startsWith("/img/")) {
    return value;
  }

  if (value.startsWith("img/")) {
    return `/${value}`;
  }

  /*
   * Fichiers stockés et servis par le serveur API.
   *
   * Exemples :
   * /uploads/techniques/image.jpg
   * uploads/techniques/image.jpg
   */
  const isBackendFile =
    value.startsWith("/uploads/") ||
    value.startsWith("uploads/") ||
    value.startsWith("/upload/") ||
    value.startsWith("upload/") ||
    value.startsWith("/files/") ||
    value.startsWith("files/");

  if (isBackendFile) {
    const apiRoot = String(process.env.REACT_APP_API_URL || "").replace(
      /\/+$/,
      "",
    );

    const normalizedPath = value.startsWith("/") ? value : `/${value}`;

    return apiRoot ? `${apiRoot}${normalizedPath}` : normalizedPath;
  }

  /*
   * Pour une valeur comme :
   * techniques/o-soto-gari.jpg
   *
   * on considère qu'elle appartient au dossier public du front.
   */
  return value.startsWith("/") ? value : `/${value}`;
}

export function beltTheme(ceinture = "") {
  const normalized = strip(ceinture);

  if (normalized.includes("jaune") && normalized.includes("orange")) {
    return {
      badge:
        "bg-gradient-to-r from-yellow-300 to-orange-400 text-slate-950 ring-orange-500/20",
      gradient: "from-yellow-500 via-orange-500 to-orange-600",
    };
  }

  if (normalized.includes("jaune")) {
    return {
      badge: "bg-yellow-300 text-yellow-950 ring-yellow-500/20",
      gradient: "from-yellow-500 via-amber-500 to-orange-400",
    };
  }

  if (normalized.includes("orange")) {
    return {
      badge: "bg-orange-300 text-orange-950 ring-orange-500/20",
      gradient: "from-orange-600 via-orange-500 to-amber-400",
    };
  }

  if (normalized.includes("verte")) {
    return {
      badge: "bg-emerald-400 text-emerald-950 ring-emerald-500/20",
      gradient: "from-emerald-800 via-emerald-600 to-emerald-400",
    };
  }

  if (normalized.includes("bleue")) {
    return {
      badge: "bg-blue-600 text-white ring-blue-500/20",
      gradient: "from-blue-950 via-blue-700 to-blue-500",
    };
  }

  if (normalized.includes("marron")) {
    return {
      badge: "bg-amber-900 text-white ring-amber-800/30",
      gradient: "from-amber-950 via-amber-900 to-amber-700",
    };
  }

  if (normalized.includes("noire") || normalized.includes("dan")) {
    return {
      badge: "bg-neutral-950 text-white ring-neutral-700/30",
      gradient: "from-neutral-950 via-neutral-800 to-neutral-600",
    };
  }

  return {
    badge: "bg-slate-200 text-slate-800 ring-slate-300",
    gradient: "from-slate-800 via-slate-700 to-slate-500",
  };
}
